import { useRef, useState } from 'react';
import { FiDownload, FiX, FiCoffee, FiCheckCircle, FiPrinter } from 'react-icons/fi';

const ReceiptModal = ({ cart, setShowReceipt, orderType, tableNumber, customerName, discount, sendToKitchen }) => {
  const receiptRef = useRef();
  const [isSentToKitchen, setIsSentToKitchen] = useState(false);
  const [receiptNumber] = useState(`REC-${Math.floor(Math.random() * 10000000000).toString().padStart(10, '0')}`);

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  let discountAmount = 0;
  if (discount) {
    discountAmount = discount.type === 'percentage' 
      ? subtotal * (discount.value / 100) 
      : discount.value;
  }
  const taxableAmount = subtotal - discountAmount;
  const tax = taxableAmount * 0.08;
  const total = taxableAmount + tax;

  // Get formatted date and time
  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Enhanced download function
  const handleDownload = () => {
    const receiptContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt ${receiptNumber}</title>
        <style>
          body { 
            font-family: 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 400px;
            margin: 0 auto;
            padding: 20px;
          }
          .header { text-align: center; margin-bottom: 20px; }
          .title { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
          .restaurant { font-size: 18px; font-weight: 600; margin-bottom: 10px; }
          .info-section { 
            border-top: 1px dashed #ddd; 
            border-bottom: 1px dashed #ddd; 
            padding: 15px 0;
            margin: 15px 0;
          }
          .info-row { 
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .items-section { margin-bottom: 20px; }
          .item-row { 
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .note { 
            font-size: 12px;
            color: #666;
            font-style: italic;
            margin-left: 15px;
          }
          .total-section { 
            border-top: 1px dashed #ddd;
            border-bottom: 1px dashed #ddd;
            padding: 15px 0;
            margin: 20px 0;
          }
          .footer { 
            text-align: center;
            color: #666;
            font-style: italic;
            margin-top: 20px;
          }
          .discount { color: #e53e3e; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="title">Receipt</h1>
          <div class="restaurant">Restaurant Name</div>
          <div>123 Main Street</div>
          <div>City, State 12345</div>
          <div>Tel: (555) 123-4567</div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span>Order Type:</span>
            <span>${orderType}</span>
          </div>
          ${orderType === 'Dine In' ? `
          <div class="info-row">
            <span>Table #:</span>
            <span>${tableNumber || 'N/A'}</span>
          </div>` : ''}
          ${orderType === 'Take Out' ? `
          <div class="info-row">
            <span>Customer:</span>
            <span>${customerName || 'N/A'}</span>
          </div>` : ''}
          <div class="info-row">
            <span>Receipt #:</span>
            <span>${receiptNumber}</span>
          </div>
          <div class="info-row">
            <span>Date:</span>
            <span>${getCurrentDate()}</span>
          </div>
          <div class="info-row">
            <span>Time:</span>
            <span>${getCurrentTime()}</span>
          </div>
        </div>

        <div class="items-section">
          <h3>Order Items</h3>
          ${cart.map(item => `
            <div class="item-row">
              <div>
                <span>${item.quantity}x ${item.name}</span>
                ${item.specialInstructions ? `
                <div class="note">Note: ${item.specialInstructions}</div>` : ''}
              </div>
              <span>$${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          `).join('')}
        </div>

        <div class="total-section">
          <div class="info-row">
            <span>Subtotal:</span>
            <span>$${subtotal.toFixed(2)}</span>
          </div>
          ${discount ? `
          <div class="info-row">
            <span>Discount (${discount.reason || 'Promotional'}):</span>
            <span class="discount">-$${discountAmount.toFixed(2)}</span>
          </div>` : ''}
          <div class="info-row">
            <span>Tax (8%):</span>
            <span>$${tax.toFixed(2)}</span>
          </div>
          <div class="info-row" style="font-weight: bold; font-size: 1.1em;">
            <span>Total:</span>
            <span>$${total.toFixed(2)}</span>
          </div>
        </div>

        <div class="footer">
          Thank you for your business!<br>
          Please come again
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([receiptContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt_${receiptNumber}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Print function
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt ${receiptNumber}</title>
          <style>
            body { 
              font-family: Arial, sans-serif;
              line-height: 1.4;
              color: #000;
              max-width: 80mm;
              margin: 0 auto;
              padding: 10px;
            }
            .header { text-align: center; margin-bottom: 15px; }
            .title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
            .restaurant { font-size: 16px; font-weight: 600; margin-bottom: 5px; }
            .info-section { 
              border-top: 1px dashed #000; 
              border-bottom: 1px dashed #000; 
              padding: 10px 0;
              margin: 10px 0;
              font-size: 12px;
            }
            .info-row { 
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            }
            .items-section { margin-bottom: 15px; font-size: 12px; }
            .item-row { 
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            }
            .note { 
              font-size: 10px;
              color: #555;
              font-style: italic;
              margin-left: 10px;
            }
            .total-section { 
              border-top: 1px dashed #000;
              border-bottom: 1px dashed #000;
              padding: 10px 0;
              margin: 15px 0;
              font-size: 12px;
            }
            .footer { 
              text-align: center;
              color: #555;
              font-style: italic;
              margin-top: 15px;
              font-size: 11px;
            }
            .discount { color: #f00; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 class="title">Receipt</h1>
            <div class="restaurant">Restaurant Name</div>
            <div>123 Main Street</div>
            <div>City, State 12345</div>
            <div>Tel: (555) 123-4567</div>
          </div>

          <div class="info-section">
            <div class="info-row">
              <span>Order Type:</span>
              <span>${orderType}</span>
            </div>
            ${orderType === 'Dine In' ? `
            <div class="info-row">
              <span>Table #:</span>
              <span>${tableNumber || 'N/A'}</span>
            </div>` : ''}
            ${orderType === 'Take Out' ? `
            <div class="info-row">
              <span>Customer:</span>
              <span>${customerName || 'N/A'}</span>
            </div>` : ''}
            <div class="info-row">
              <span>Receipt #:</span>
              <span>${receiptNumber}</span>
            </div>
            <div class="info-row">
              <span>Date:</span>
              <span>${getCurrentDate()}</span>
            </div>
            <div class="info-row">
              <span>Time:</span>
              <span>${getCurrentTime()}</span>
            </div>
          </div>

          <div class="items-section">
            <h3>Order Items</h3>
            ${cart.map(item => `
              <div class="item-row">
                <div>
                  <span>${item.quantity}x ${item.name}</span>
                  ${item.specialInstructions ? `
                  <div class="note">Note: ${item.specialInstructions}</div>` : ''}
                </div>
                <span>$${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            `).join('')}
          </div>

          <div class="total-section">
            <div class="info-row">
              <span>Subtotal:</span>
              <span>$${subtotal.toFixed(2)}</span>
            </div>
            ${discount ? `
            <div class="info-row">
              <span>Discount (${discount.reason || 'Promotional'}):</span>
              <span class="discount">-$${discountAmount.toFixed(2)}</span>
            </div>` : ''}
            <div class="info-row">
              <span>Tax (8%):</span>
              <span>$${tax.toFixed(2)}</span>
            </div>
            <div class="info-row" style="font-weight: bold; font-size: 1.1em;">
              <span>Total:</span>
              <span>$${total.toFixed(2)}</span>
            </div>
          </div>

          <div class="footer">
            Thank you for your business!<br>
            Please come again
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleSendToKitchen = () => {
    sendToKitchen();
    setIsSentToKitchen(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        {/* Receipt Header */}
        <div className="bg-gray-800 text-white p-4 flex justify-between items-center">
          <div className="flex items-center">
            <FiCoffee className="mr-2" size={20} />
            <div>
              <h1 className="text-xl font-bold">Order Receipt</h1>
              <p className="text-xs opacity-90">{receiptNumber}</p>
            </div>
          </div>
          <button
            onClick={() => setShowReceipt(false)}
            className="text-white hover:text-gray-200"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Receipt Content */}
        <div className="p-6 overflow-y-auto flex-grow" ref={receiptRef}>
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold">Restaurant Name</h2>
            <p className="text-sm text-gray-600">123 Main Street, City, State 12345</p>
          </div>

          <div className="space-y-4">
            {/* Order Info */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-gray-500">Order Type:</div>
              <div className="font-medium">{orderType}</div>
              
              {orderType === 'Dine In' && (
                <>
                  <div className="text-gray-500">Table #:</div>
                  <div className="font-medium">{tableNumber || 'N/A'}</div>
                </>
              )}
              
              {orderType === 'Take Out' && (
                <>
                  <div className="text-gray-500">Customer:</div>
                  <div className="font-medium">{customerName || 'N/A'}</div>
                </>
              )}
              
              <div className="text-gray-500">Date:</div>
              <div className="font-medium">{getCurrentDate()}</div>
              
              <div className="text-gray-500">Time:</div>
              <div className="font-medium">{getCurrentTime()}</div>
            </div>

            {/* Order Items */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-medium mb-3">Items Ordered</h3>
              <div className="space-y-3">
                {cart.map(item => (
                  <div key={item.cartId} className="flex justify-between">
                    <div>
                      <p>{item.quantity}x {item.name}</p>
                      {item.specialInstructions && (
                        <p className="text-xs text-gray-500 italic">Note: {item.specialInstructions}</p>
                      )}
                    </div>
                    <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Totals */}
            <div className="border-t border-gray-200 pt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              
              {discount && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium text-red-500">-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="text-gray-600">Tax (8%):</span>
                <span className="font-medium">${tax.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between font-bold text-lg border-t border-gray-200 pt-2 mt-2">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          {isSentToKitchen ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center text-green-600">
                <FiCheckCircle className="mr-2" />
                <span>Order sent to kitchen!</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleDownload}
                  className="flex items-center justify-center py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <FiDownload className="mr-2" />
                  Download
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center justify-center py-2 px-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  <FiPrinter className="mr-2" />
                  Print
                </button>
              </div>
              <button
                onClick={() => setShowReceipt(false)}
                className="w-full py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={handleDownload}
                className="flex items-center justify-center py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <FiDownload className="mr-2" />
                Download
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center justify-center py-2 px-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
              >
                <FiPrinter className="mr-2" />
                Print
              </button>
              <button
                onClick={handleSendToKitchen}
                className="flex items-center justify-center py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                <FiCoffee className="mr-2" />
                Send
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;